import axios from 'axios';
import { useEffect, useState } from 'react';
import { notification } from 'antd';
import { serverURL } from '../../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import SoldOutPage from './SoludOut/SoldOutPage';
import { BRANDED_DOMAINS, UNBRANDED_DOMAINS } from 'src/enums/ERoutes';
import UserRegistrationPage from '../UserRegistration/UserRegistrationPage';
import { Features } from '@/types/TReseller';

interface ResellerInfo {
	id: string;
	businessName?: string;
	logo?: string;
	emailAddress?: string;
	auto_scale_receptionists?: boolean;
	unConfiguredInboundAgents?: number;
	unConfiguredOutboundAgents?: number;
	features?: Features;
}

interface RegisterPageWrapperProps {
	test?: boolean;
	isCampaign?: boolean;
	isStandaloneCampaign?: boolean;
}

const RegisterPageWrapper: React.FC<RegisterPageWrapperProps> = ({
	test = false,
	isCampaign = false,
}) => {
	const [resellerInfo, setResellerInfo] = useState<ResellerInfo | null>(null);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [isLoadingResellerData, setIsLoadingResellerData] = useState(true);
	const [isLoadingReceptionistData, setIsLoadingReceptionistData] = useState(true);

	useEffect(() => {
		if (typeof window !== 'undefined' && resellerInfo?.businessName) {
			try {
				// Update the document title (tab title)
				document.title = `${resellerInfo.businessName} Portal Login`;

				// Update the meta description
				const metaDescription = document.querySelector('meta[name="description"]');
				if (metaDescription) {
					metaDescription.setAttribute(
						'content',
						`${resellerInfo.businessName} Dashboard/Portal login`,
					);
				}

				// If you still need to update an element with id 'page-title'
				const pageTitle = document.getElementById('page-title');
				if (pageTitle) {
					pageTitle.textContent = `${resellerInfo.businessName} Portal Login`;
				}
			} catch (err) {
				console.error('Error updating page metadata:', err);
			}
		}
	}, [resellerInfo?.businessName]);

	useEffect(() => {
		const fetchReseller = async (resellerCode: string) => {
			try {
				const response = await axios.post(`${serverURL}/get-reseller-from-reseller-code`, {
					resellerCode,
				});

				if (response?.data?.reseller?.logo) {
					// Fetch the logo image
					const logoResponse = await fetch(`${serverURL}/download_reseller_logo`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							key: response?.data?.reseller?.logo,
						}),
					});

					const blob = await logoResponse.blob();
					const objectUrl = URL.createObjectURL(blob);
					setImageUrl(objectUrl);
				}

				setResellerInfo(response.data.reseller);
			} catch (err) {
				console.error(err);
				notification.error({
					message: 'Error',
					description: 'Something went wrong',
				});
			} finally {
				setLoading(false);
			}
		};

		const resellerCode = searchParams.get('reseller_code') || searchParams.get('code');
		if (resellerCode) {
			setLoading(true);
			fetchReseller(resellerCode);
		} else {
			setLoading(false);
		}
	}, [searchParams]);

	useEffect(() => {
		const fetchResellerByDomain = async () => {
			try {
				const hostname = window.location.hostname.replace(/^www\./, '');
				const isCustomDomain =
					!BRANDED_DOMAINS.includes(hostname) && !UNBRANDED_DOMAINS.includes(hostname);

				let response = null;

				if (isCustomDomain) {
					try {
						response = await axios.post(`${serverURL}/get-reseller-from-domain`, {
							domain: hostname,
						});
						setResellerInfo(response.data.reseller);
					} catch (error) {
						console.error('Error fetching reseller from domain:', error);
						notification.error({
							message: 'Error',
							description: 'Something went wrong',
						});
					}
				}

				if (response?.data?.reseller?.logo) {
					// Fetch the logo image
					const logoResponse = await fetch(`${serverURL}/download_reseller_logo`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							key: response?.data?.reseller?.logo,
						}),
					});

					const blob = await logoResponse.blob();
					const objectUrl = URL.createObjectURL(blob);
					setImageUrl(objectUrl);
				}
			} catch (err) {
				console.error(err);
				notification.error({
					message: 'Error',
					description: 'Something went wrong',
				});
			} finally {
				setIsLoadingResellerData(false);
			}
		};

		setIsLoadingResellerData(true);
		fetchResellerByDomain();
	}, []);

	// Add effect to check receptionist availability
	useEffect(() => {
		const checkReceptionistAvailability = async () => {
			if (!resellerInfo) {
				return;
			}

			try {
				// Add your API call here to check receptionist availability
				// This is just a placeholder - replace with actual API call
				await new Promise((resolve) => setTimeout(resolve, 500));
				setIsLoadingReceptionistData(false);
			} catch (error) {
				console.error('Error checking receptionist availability:', error);
				setIsLoadingReceptionistData(false);
			}
		};

		if (!isLoadingResellerData) {
			setIsLoadingReceptionistData(true);
			checkReceptionistAvailability();
		}
	}, [resellerInfo, isLoadingResellerData]);

	// Update loading state to consider both API calls
	useEffect(() => {
		setLoading(isLoadingResellerData || isLoadingReceptionistData);
	}, [isLoadingResellerData, isLoadingReceptionistData]);

	if (loading) {
		return (
			<div className="flex w-screen h-screen items-center justify-center">
				<CircularProgress />
			</div>
		);
	}

	return (
		<div>
			{/* <Toaster position="top-center" reverseOrder={false} /> */}

			{resellerInfo &&
				((resellerInfo &&
					!isCampaign &&
					(resellerInfo.unConfiguredInboundAgents! > 0 || resellerInfo.auto_scale_receptionists)) ||
				(isCampaign && resellerInfo.unConfiguredOutboundAgents! > 0) ||
				(test && !isCampaign) ? (
					<UserRegistrationPage
						debrand
						trialForReseller={test}
						handleComplete={() => {
							navigate('/login');
						}}
						resellerId={resellerInfo.id}
						resellerBusinessName={resellerInfo.businessName}
						features={resellerInfo.features}
						auto_scale={resellerInfo.auto_scale_receptionists}
						resellerLogoUrl={imageUrl || undefined}
						agentType={'Inbound'}
					/>
				) : (
					<SoldOutPage
						registerText={isCampaign ? 'Campaign' : 'Receptionist'}
						supportEmail={resellerInfo.emailAddress}
						companyName={resellerInfo.businessName}
					/>
				))}
		</div>
	);
};

export default RegisterPageWrapper;
